/* קריאת קבצי Excel של בנק וכרטיס אשראי, בלי מודל: הסכומים והתאריכים נלקחים מהקובץ עצמו,
   ולכן הסך תמיד זהה לדוח. המודל משמש אחר כך רק לסיווג שמות.
   פורמטים: עו"ש (עמודות "סכום פעולה" + "חיוב/זיכוי"), וישראכרט ("עסקאות למועד חיוב"). */
import { S } from '../state.js';
import { createRtlFixer, isVisualOrder, reverseKeepRuns, mNorm } from '../../lib/text.js';
import { fISO } from '../../lib/format.js';
import { CARD_RX } from '../tx.js';

export function xlSerialToDate(serial) {
  // אפס הספירה של אקסל הוא 30.12.1899, וזה כבר מכיל את באג השנה המעוברת של 1900
  return fISO(new Date(1899, 11, 30 + Number(serial)));
}

export function fixDateStr(s) {
  if (!s) return null;
  if (s instanceof Date && !isNaN(s.getTime())) return fISO(s);
  s = s.toString().trim();
  if (/^[A-Z][a-z]{2} [A-Z][a-z]{2} \d{1,2} \d{4}/.test(s)) { const dd = new Date(s); if (!isNaN(dd.getTime())) return fISO(dd); }
  const sl = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (sl) { let yy = parseInt(sl[3], 10); if (yy < 100) yy += 2000; return yy + '-' + sl[2].padStart(2, '0') + '-' + sl[1].padStart(2, '0'); }
  if (/^\d{5}$/.test(s) && parseInt(s, 10) > 40000 && parseInt(s, 10) < 50000) return xlSerialToDate(parseInt(s, 10));
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/);
  if (m) { let y = parseInt(m[3], 10); if (y < 100) y += 2000; return y + '-' + m[2].padStart(2, '0') + '-' + m[1].padStart(2, '0'); }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}

/* ── מטבע זר ── */
const FX_SYM = { $: 'USD', USD: 'USD', '€': 'EUR', EUR: 'EUR', '£': 'GBP', GBP: 'GBP' };
const FX_DEFAULT = { USD: 3.7, EUR: 4.0, GBP: 4.7 };
export function fxIsForeign(c) {
  c = String(c || '').trim();
  return !!c && c !== '₪' && c.indexOf('ILS') < 0 && c.indexOf('ש"ח') < 0 && c.indexOf('שח') < 0;
}
export function fxCode(c) {
  c = String(c || '').trim();
  return FX_SYM[c] || FX_SYM[c.toUpperCase()] || c;
}
/** השער מעסקאות קודמות באותו מטבע: הסכום בשקלים חלקי הסכום המקורי (חציון של האחרונות) */
function fxHistoryRate(code) {
  const r = [];
  S.tx.forEach((t) => {
    const m = String(t.orig_amount || '').match(/^([\d.]+)\s+(\S+)$/);
    if (!m || fxCode(m[2]) !== code) return;
    const o = parseFloat(m[1]), a = Math.abs(Number(t.amount) || 0);
    if (o > 0 && a > 0) r.push({ d: String(t.date || ''), v: a / o });
  });
  r.sort((a, b) => (a.d < b.d ? 1 : -1));
  const v = r.slice(0, 10).map((x) => x.v).sort((a, b) => a - b);
  return v.length ? v[Math.floor(v.length / 2)] : 0;
}
/* המרת עסקאות זרות שטרם נקלטו:
   1. מהסיכום בשקלים של החלק (השער של ישראכרט עצמה), כשיש מטבע זר אחד בלבד
   2. אחרת, מהשער של עסקאות קודמות באותו מטבע
   3. אחרת, ברירת מחדל גסה, מסומנת כהערכה לבדיקה */
function fxConvertPending(list, ptot) {
  const fx = list.filter((r) => r.notYetBilled && r.fxCur);
  if (!fx.length) return;
  const ils = list.filter((r) => r.notYetBilled && !r.fxCur).reduce((s, r) => s + (r.rowType === 'income' ? -r.amount : r.amount), 0);
  const codes = {};
  fx.forEach((r) => (codes[r.fxCur] = 1));
  let implied = 0;
  if (Object.keys(codes).length === 1 && !isNaN(ptot)) {
    const sumF = fx.reduce((s, r) => s + (r.rowType === 'income' ? -r.fxAmt : r.fxAmt), 0);
    if (sumF > 0) implied = (ptot - ils) / sumF;
  }
  fx.forEach((r) => {
    const hist = fxHistoryRate(r.fxCur);
    let rate = 0, src = '';
    if (implied > 0.2 && implied < 20 && (!hist || Math.abs(implied / hist - 1) < 0.25)) { rate = implied; src = 'sum'; }
    else if (hist) { rate = hist; src = 'hist'; }
    else { rate = FX_DEFAULT[r.fxCur] || 0; src = 'default'; }
    // אין שער: לא שומרים סכום זר כאילו הוא בשקלים. העסקה תגיע בדוח הבא, כבר בשקלים
    if (!rate) { r.fxDrop = true; return; }
    r.amount = Math.round(r.fxAmt * rate * 100) / 100;
    r.fxRate = rate;
    r.fxSrc = src;
  });
}

/** השנה של "לחיוב ב-10.01": מהתאריך, אחרת מכותרת הדוח, אחרת השנה שמקרבת את התאריך להיום */
function cardChargeYear(rows, cm) {
  if (cm[3]) return cm[3].length === 2 ? '20' + cm[3] : cm[3];
  const mon = parseInt(cm[2], 10);
  const MN = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
  for (let r = 0; r < Math.min(rows.length, 12); r++) {
    const m = rows[r].join(' ').match(/(ינואר|פברואר|מרץ|אפריל|מאי|יוני|יולי|אוגוסט|ספטמבר|אוקטובר|נובמבר|דצמבר)\s+(20\d\d)/);
    if (m && MN.indexOf(m[1]) + 1 === mon) return m[2];
  }
  const t = new Date();
  let best = t.getFullYear(), bd = 1e15;
  [t.getFullYear() - 1, t.getFullYear(), t.getFullYear() + 1].forEach((y) => {
    const d = Math.abs(new Date(y, mon - 1, parseInt(cm[1], 10)) - t);
    if (d < bd) { bd = d; best = y; }
  });
  return String(best);
}

function cellDate(rawDate) {
  if (rawDate instanceof Date) return fISO(rawDate);
  const dStr = rawDate.toString().trim();
  if (/^\d+$/.test(dStr)) return xlSerialToDate(parseInt(dStr, 10));
  if (/^\d{4}-\d{2}-\d{2}/.test(dStr)) return dStr.slice(0, 10);
  if (/^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}/.test(dStr)) {
    const parts = dStr.split(/[/.-]/);
    const y = parts[2].length === 2 ? '20' + parts[2] : parts[2].slice(0, 4);
    let p0 = parseInt(parts[0], 10), p1 = parseInt(parts[1], 10);
    if (p1 > 12 && p0 <= 12) { const tmp = p0; p0 = p1; p1 = tmp; } // סדר אמריקאי
    return y + '-' + String(p1).padStart(2, '0') + '-' + String(p0).padStart(2, '0');
  }
  const parsed = new Date(dStr);
  return isNaN(parsed.getTime()) ? '' : fISO(parsed);
}

const stripMarks = (s) => s.replace(/[\u202a-\u202e\u200b-\u200f]/g, '').trim();
const money = (v) => (v == null ? '' : v.toString().replace(/[₪,]/g, '').trim());

/* ── עו"ש ── */
function parseBankSheet(rows, headerRow, ctx, rtl) {
  const hdr = rows[headerRow];
  let dCol = 0, descCol = 3, amtCol = 4, typeCol = 6, balCol = 7, refCol = -1, dPri = 0, tPri = 0;
  hdr.forEach((h, i) => {
    const hs = h.toString().trim();
    // תאריך התנועה קובע את התקופה. תאריך ערך רק אם אין אחר
    const dp = hs === 'תאריך תנועה' ? 3 : hs === 'תאריך' ? 2 : hs.indexOf('תאריך') >= 0 && hs !== 'תאריך ערך' ? 1 : hs === 'תאריך ערך' ? 0.5 : 0;
    if (dp > dPri) { dPri = dp; dCol = i; }
    const tp = hs === 'תיאור' ? 2 : hs.indexOf('פירוט') >= 0 || hs.indexOf('תיאור') >= 0 ? 1 : 0;
    if (tp > tPri) { tPri = tp; descCol = i; }
    if (hs.indexOf('סכום פעולה') >= 0) amtCol = i;
    if (hs === 'חיוב/זיכוי') typeCol = i;
    if (hs.indexOf('יתרה') >= 0) balCol = i;
    if (hs.indexOf('אסמכתא') >= 0) refCol = i;
  });
  const samples = [];
  for (let sp = headerRow + 1; sp < Math.min(rows.length, headerRow + 25); sp++) if (rows[sp] && rows[sp][descCol]) samples.push(rows[sp][descCol].toString());
  rtl.detect(samples);
  let first = '', asc = null;
  for (let r = headerRow + 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || !row[dCol]) continue;
    const dateStr = cellDate(row[dCol]);
    if (!dateStr || dateStr.indexOf('1900') >= 0) continue;
    const descCell = row[descCol] ? row[descCol].toString() : '';
    const rawDesc = stripMarks(descCell);
    if (!rawDesc) continue;
    // LRO הוא סימן ודאי לסדר תצוגה, גם בשורה בודדת
    const fixedDesc = isVisualOrder(descCell) ? reverseKeepRuns(rawDesc) : rtl.fix(rawDesc);
    const amt = money(row[amtCol]);
    if (!amt) continue;
    const amtNum = parseFloat(amt);
    if (isNaN(amtNum) || amtNum === 0) continue;
    const typeStr = row[typeCol] ? row[typeCol].toString().trim() : '';
    // הכיוון לפי עמודת חיוב/זיכוי, ורק בלעדיה לפי הסימן
    const type = typeStr === 'זיכוי' ? 'income' : typeStr === 'חיוב' ? 'expense' : amtNum > 0 ? 'income' : 'expense';
    const rec = { date: dateStr, name: fixedDesc, amount: Math.abs(amtNum), rowType: type, ref: refCol >= 0 && row[refCol] !== undefined ? String(row[refCol]).trim() : '' };
    // שורת סילוק של כרטיס אשראי: הפירוט שלה מגיע מדוח הכרטיס
    if (CARD_RX.test(fixedDesc) && type === 'expense' && Math.abs(amtNum) >= 500) rec.cardSettlement = true;
    // הקובץ יכול להיות ממוין בכל כיוון. היתרה של סוף היום היא של התנועה האחרונה באותו יום
    const balRaw = row[balCol] !== undefined && row[balCol] !== '' ? parseFloat(String(row[balCol]).replace(/[₪,]/g, '')) : NaN;
    if (!first) first = dateStr;
    else if (asc === null && dateStr !== first) asc = dateStr > first;
    const BL = ctx.bankLatest;
    if (!isNaN(balRaw) && (!BL.date || dateStr > BL.date || (dateStr === BL.date && asc === true))) ctx.bankLatest = { date: dateStr, balance: balRaw };
    ctx.extracted.push(rec);
  }
}

/* ── ישראכרט ── */
function parseCardSheet(rows, ctx, rtl, fileName) {
  rtl.reset();
  let billingStart = -1, headerRow = null;
  for (let cr = 0; cr < Math.min(rows.length, 12); cr++) {
    const cm = rows[cr].join(' ').match(/לחיוב ב-?\s*(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?/);
    if (cm) { ctx.chargeDate = cardChargeYear(rows, cm) + '-' + cm[2].padStart(2, '0') + '-' + cm[1].padStart(2, '0'); break; }
  }
  for (let r = 0; r < rows.length; r++) {
    if (rows[r].join('|').indexOf('עסקאות למועד חיוב') >= 0) billingStart = r + 1;
    if (billingStart > 0 && !headerRow && rows[r].filter((c) => c.toString().trim()).length >= 4 &&
      rows[r].some((c) => c.toString().indexOf('תאריך') >= 0)) { headerRow = rows[r]; billingStart = r + 1; }
  }
  if (billingStart <= 0) return;
  ctx.detectedSrc = 'credit';
  let dateCol = 0, nameCol = 1, amountCol = 4, currencyCol = 5, noteCol = 7, refCol = -1, c4Col = -1, origCol = -1, origCurCol = -1;
  if (headerRow) headerRow.forEach((h, i) => {
    const hs = h.toString().trim();
    if (hs.indexOf('תאריך') >= 0 && i < 3) dateCol = i;
    if (hs.indexOf('שם') >= 0 && hs.indexOf('עסק') >= 0) nameCol = i;
    if (hs.indexOf('סכום חיוב') >= 0) amountCol = i;
    if (hs.indexOf('מטבע חיוב') >= 0) currencyCol = i;
    if (hs.indexOf('פירוט נוסף') >= 0) noteCol = i;
    if (hs.indexOf('שובר') >= 0 || hs.indexOf('אסמכתא') >= 0) refCol = i;
    if (hs.indexOf('ספרות') >= 0) c4Col = i;
    if (hs.indexOf('סכום עסקה') >= 0 || hs.indexOf('סכום העסקה') >= 0) origCol = i;
    if (hs.indexOf('מטבע עסקה') >= 0 || hs.indexOf('מטבע העסקה') >= 0) origCurCol = i;
  });
  // 4 ספרות הכרטיס: בשורות הכותרת, או בשם הקובץ של ישראכרט (2282_10_2026.xlsx)
  let card4 = '';
  for (let hr = 0; hr < Math.min(rows.length, 12) && !card4; hr++) {
    const mc = rows[hr].join(' ').match(/(?:כרטיס|המסתיים|מסתיים|ספרות|מסטרקארד|מאסטרקארד|mastercard|ויזה|visa|אמריקן|amex|דיינרס|diners|ישראכרט|לאומי ?קארד|מקס|max|כאל)[^\d]{0,40}(\d{4})(?!\d)/i);
    if (mc) card4 = mc[1];
  }
  if (!card4 && fileName) { const mf = String(fileName).match(/^(\d{4})[_\s-]/); if (mf) card4 = mf[1]; }

  const samples = [];
  for (let sp = billingStart; sp < Math.min(rows.length, billingStart + 30); sp++) if (rows[sp] && rows[sp][nameCol]) samples.push(rows[sp][nameCol].toString());
  rtl.detect(samples);

  /* "עסקאות שטרם נקלטו": כסף שכבר הוצא אך טרם נקלט סופית. אין להן שובר, והסכום עוד יכול להשתנות.
     רכישה כזו שייכת למחזור שבו בוצעה; רק רכישה אחרי תאריך החיוב תיפול למחזור הבא. */
  let pendStart = -1, pendEnd = -1;
  for (let pr = 0; pr < rows.length; pr++) {
    const ps = rows[pr].join('|');
    if (ps.indexOf('עסקאות שטרם נקלטו') >= 0 && pendStart < 0) pendStart = pr + 2;
    if (pendStart > 0 && pendEnd < 0 && (ps.indexOf('סה"כ עסקאות שטרם') >= 0 || ps.indexOf('עסקאות למועד חיוב') >= 0)) pendEnd = pr;
  }
  if (pendStart > 0 && pendEnd > pendStart) {
    for (let pi = pendStart; pi < pendEnd; pi++) {
      const prow = rows[pi];
      const pdate = prow[0] ? String(prow[0]).trim() : '';
      const pname = prow[1] ? String(prow[1]).trim() : '';
      const pamt = money(prow[2]);
      if (!pdate || !pname || !pamt || pname.indexOf('סה"כ') >= 0) continue;
      const pf = parseFloat(pamt);
      if (isNaN(pf) || pf === 0) continue;
      const pIso = fixDateStr(pdate) || pdate;
      let pBill = ctx.chargeDate;
      if (ctx.chargeDate && pIso > ctx.chargeDate) {
        const cdd = new Date(ctx.chargeDate + 'T12:00:00');
        pBill = fISO(new Date(cdd.getFullYear(), cdd.getMonth() + 1, cdd.getDate()));
      }
      const pcur = prow[3] ? String(prow[3]).trim() : '';
      const rec = { date: pdate, name: rtl.fix(pname), amount: Math.abs(pf), rowType: pf < 0 ? 'income' : 'expense', notYetBilled: true, billMonth: pBill, card4, ref: '' };
      if (fxIsForeign(pcur)) { rec.fxCur = fxCode(pcur); rec.fxAmt = Math.abs(pf); rec.orig = String(prow[2]).replace(/,/g, '').trim() + ' ' + pcur; }
      ctx.extracted.push(rec);
    }
    // שורת הסיכום בשקלים כוללת גם את הזרות, ולכן ממנה נגזר השער של ישראכרט עצמה
    let ptot = NaN;
    if (rows[pendEnd] && String(rows[pendEnd].join('|')).indexOf('סה"כ עסקאות שטרם') >= 0) ptot = parseFloat(money(rows[pendEnd][2]));
    fxConvertPending(ctx.extracted, ptot);
  }

  for (let r = billingStart; r < rows.length; r++) {
    const row = rows[r];
    const date = row[dateCol] ? row[dateCol].toString().trim() : '';
    const name = row[nameCol] ? stripMarks(row[nameCol].toString()) : '';
    const amt = money(row[amountCol]);
    const curr = row[currencyCol] ? row[currencyCol].toString().trim() : '';
    if (!date || !name || !amt) continue;
    if (name.indexOf('סה"כ') >= 0 || name.indexOf("סה''כ") >= 0 || name.indexOf('סהכ') >= 0) continue;
    if (curr && curr !== '₪' && curr.indexOf('ILS') < 0 && curr.indexOf('שח') < 0 && curr.indexOf('ש"ח') < 0) { ctx.fxBilledSkipped++; continue; }
    const origAmt = money(row[2]);
    let finalAmt = parseFloat(amt);
    if (isNaN(finalAmt) || finalAmt === 0) finalAmt = parseFloat(origAmt) || 0;
    const note = row[noteCol] ? row[noteCol].toString() : '';
    const inst = note.match(/תשלום\s*(\d+)\s*מתוך\s*(\d+)/);
    const rec = {
      date, name: rtl.fix(name), amount: Math.abs(finalAmt), rowType: finalAmt < 0 ? 'income' : 'expense', standing: /הוראת\s*קבע/.test(note),
      ref: refCol >= 0 && row[refCol] !== undefined ? String(row[refCol]).trim() : '',
      card4: (c4Col >= 0 && row[c4Col] ? String(row[c4Col]).replace(/\D/g, '').slice(-4) : '') || card4,
    };
    const oc = origCurCol >= 0 ? String(row[origCurCol] || '').trim() : '';
    if (origCol >= 0 && fxIsForeign(oc)) rec.orig = String(row[origCol]).replace(/,/g, '').trim() + ' ' + oc;
    if (inst) {
      rec.instNum = parseInt(inst[1], 10);
      rec.instTotal = parseInt(inst[2], 10);
      // כשהעסקה עוד "שטרם נקלטה" היא מופיעה בתאריך הרכישה ובסכום המלא. שומרים את שניהם לזיהוי
      rec.buyDate = date;
      rec.fullAmt = Math.abs(parseFloat(origAmt) || 0);
      // חיוב תשלומים שייך לחודש החיוב, לא לתאריך הרכישה
      if (ctx.chargeDate) rec.date = ctx.chargeDate;
    }
    ctx.extracted.push(rec);
  }
}

/** ישראכרט מציגה לפעמים עסקה גם ב"שטרם נקלטו" וגם ב"למועד חיוב" של אותו קובץ. השורה הממתינה יורדת */
function dropPendingTwins(extracted) {
  const billed = extracted.filter((r) => !r.notYetBilled);
  const used = {};
  let dropped = 0;
  const out = extracted.filter((p) => {
    if (!p.notYetBilled) return true;
    const pn = mNorm(p.name), pa = Math.round(Math.abs(Number(p.amount) || 0) * 100);
    let hit = -1;
    for (let bi = 0; bi < billed.length; bi++) {
      if (used[bi]) continue;
      const b = billed[bi];
      if (String(b.buyDate || b.date).trim() !== String(p.date).trim()) continue;
      // במטבע זר משווים את הסכום המקורי, כי הסכום בשקלים שלנו הוא המרה משוערת
      const sameAmt = p.orig && b.orig ? String(p.orig).replace(/\s+/g, ' ') === String(b.orig).replace(/\s+/g, ' ')
        : Math.round(Math.abs(Number(b.fullAmt || b.amount) || 0) * 100) === pa;
      if (!sameAmt) continue;
      const bn = mNorm(b.name);
      if (pn.indexOf(bn) === 0 || bn.indexOf(pn) === 0 || (pn.length >= 6 && pn.slice(0, 8) === bn.slice(0, 8))) { hit = bi; break; }
    }
    if (hit >= 0) { used[hit] = 1; dropped++; return false; }
    return true;
  });
  return { out, dropped };
}

/**
 * @param XLSX ספריית SheetJS
 * @param wb workbook
 * @returns {{rows:Array, csv?:string, chargeDate, installments, deferred, settlements, bankLatest, detectedSrc, notes}}
 */
export function parseWorkbook(XLSX, wb, fileName) {
  const ctx = { extracted: [], chargeDate: '', bankLatest: { date: '', balance: null }, detectedSrc: '', fxBilledSkipped: 0 };
  const rtl = createRtlFixer();
  wb.SheetNames.forEach((shName) => {
    // raw:true משאיר תאי תאריך כ-Date. עם raw:false הם מעוצבים M/D/Y ומתפרשים הפוך
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[shName], { header: 1, raw: true, defval: '' });
    let bankHeader = -1;
    for (let br = 0; br < Math.min(rows.length, 20); br++) {
      const s = rows[br].join('|');
      if (s.indexOf('סכום פעולה') >= 0 && s.indexOf('חיוב/זיכוי') >= 0) { bankHeader = br; break; }
    }
    if (bankHeader >= 0) { ctx.detectedSrc = 'cash'; parseBankSheet(rows, bankHeader, ctx, rtl); }
    else parseCardSheet(rows, ctx, rtl, fileName);
  });

  const { out, dropped } = dropPendingTwins(ctx.extracted);
  const fxDropped = out.filter((r) => r.fxDrop).length;
  const extracted = out.filter((r) => !r.fxDrop);

  const notes = [];
  if (fxDropped || ctx.fxBilledSkipped) notes.push('💱 ' + (fxDropped + ctx.fxBilledSkipped) + ' עסקאות במטבע זר לא נשמרו: אין להן סכום בשקלים. הן ייכנסו כשהדוח יציג אותן בשקלים');
  if (dropped) notes.push('🧹 ' + dropped + ' עסקאות הופיעו בדוח פעמיים (שטרם נקלטו + למועד חיוב) ונספרו פעם אחת');

  if (!extracted.length) {
    let csv = '';
    wb.SheetNames.forEach((n) => (csv += XLSX.utils.sheet_to_csv(wb.Sheets[n]) + '\n'));
    return { rows: [], csv, notes };
  }

  // חיובי חו"ל נדחים: "ביצעת N עסקאות בסך X", יחויבו בחודש שאחרי
  let deferred = null;
  wb.SheetNames.forEach((sn) => {
    const dm = XLSX.utils.sheet_to_csv(wb.Sheets[sn]).match(/ביצעת\s*(\d+)\s*עסקאות\s*בסך\s*₪?\s*([\d,.]+)/);
    if (dm && !deferred) {
      let due = ctx.chargeDate ? new Date(ctx.chargeDate + 'T12:00:00') : new Date();
      due = new Date(due.getFullYear(), due.getMonth() + 1, due.getDate());
      deferred = { count: parseInt(dm[1], 10), amount: parseFloat(dm[2].replace(/,/g, '')), date: fISO(due) };
    }
  });

  const rows = extracted.map((r) => ({
    date: fixDateStr(r.date) || r.date,
    name: r.name,
    amount: r.amount,
    type: r.rowType,
    instNum: r.instNum, instTotal: r.instTotal,
    cardSettlement: r.cardSettlement,
    notYetBilled: r.notYetBilled, billMonth: r.billMonth, fxRate: r.fxRate, fxSrc: r.fxSrc, standing: r.standing,
    ref: r.ref || '', card4: r.card4 || '', raw: r.name,
    // בתשלומים: הסכום המלא ותאריך הרכישה, קבועים בכל הדוחות של אותה עסקה
    orig: r.orig || (r.instNum && r.fullAmt ? r.fullAmt + ' ILS@' + (fixDateStr(r.buyDate) || r.buyDate) : ''),
  }));
  return {
    rows,
    chargeDate: ctx.chargeDate,
    installments: extracted.filter((r) => r.instNum && r.instNum < r.instTotal),
    deferred,
    settlements: extracted.filter((r) => r.cardSettlement),
    bankLatest: ctx.bankLatest,
    detectedSrc: ctx.detectedSrc,
    notes,
  };
}
